// Tester feedback: a private link per tester (spanning one or more products) where non-users write
// free-text lists of what they noticed. The owner turns each point into an item, links it to an existing
// one or dismisses it; the tester sees a status derived from that.
import { z } from "zod";
import type { StatusCategory } from "./items.ts";

export const FEEDBACK_LIMITS = {
  /** Points per submission. */
  points: 50,
  /** Characters per point (after splitting). */
  pointChars: 4000,
  /** Characters of the whole message. */
  textChars: 50_000,
  imagesPerPoint: 10,
  imagesPerBatch: 50,
  /** Bytes per uploaded image (the client downscales before uploading). */
  imageBytes: 8 * 1024 * 1024,
  labelChars: 60,
  noteChars: 1000,
} as const;

export const FEEDBACK_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
export type FeedbackImageType = (typeof FEEDBACK_IMAGE_TYPES)[number];

// ---------------------------------------------------------------------------------------------------------
// Splitting free text into points. Shared by the composer preview and the server, so what the tester sees
// before sending is exactly what arrives. The composer writes Markdown (bold, lists, sub-lists); plain text
// from older clients goes through the same rules.
// ---------------------------------------------------------------------------------------------------------

/**
 * Image placeholder the composer inserts at the caret: "[Bild 2]" (de) or "[Image 2]" (en). The rich editor
 * stores brackets escaped ("\[Bild 2\]"), so the backslashes are optional.
 */
export const IMAGE_MARKER = /\\?\[(?:Bild|Image|Screenshot) (\d{1,3})\\?\]/g;
const BULLET = /^([ \t]*)((?:[-*+•·–]|\d{1,3}[.)])[ \t]+)/;
const ONLY_MARKERS = /^(?:\s*\\?\[(?:Bild|Image|Screenshot) \d{1,3}\\?\]\s*)+$/;
/** Markdown hard line break at the end of a line (Shift+Enter in the editor): two spaces or a backslash. */
const HARD_BREAK = /(?: {2,}|(?<!\\)\\)$/;

export function imageMarker(n: number, locale: "en" | "de"): string {
  return `[${locale === "de" ? "Bild" : "Image"} ${n}]`;
}

export interface FeedbackPoint {
  /**
   * The point's text as Markdown, without its own bullet and without image markers. Sub-points stay in it as a
   * nested list. May be empty when it only has images.
   */
  text: string;
  /** Image marker numbers in order of appearance (deduplicated). */
  images: number[];
}

function markersIn(line: string): number[] {
  return [...line.matchAll(IMAGE_MARKER)].map((m) => Number(m[1]));
}

/** Width of the leading whitespace (a tab counts as four columns). */
function indentOf(line: string): number {
  let n = 0;
  for (const ch of line) {
    if (ch === " ") n++;
    else if (ch === "\t") n += 4;
    else break;
  }
  return n;
}

/** Removes up to `cols` columns of leading whitespace. */
function dedent(line: string, cols: number): string {
  let n = 0;
  let i = 0;
  while (i < line.length && n < cols) {
    const ch = line[i];
    if (ch === " ") n++;
    else if (ch === "\t") n += 4;
    else break;
    i++;
  }
  return line.slice(i);
}

/** Drops image markers and tidies whitespace while keeping what Markdown needs: indentation and hard breaks. */
function tidy(text: string): string {
  return text
    .split("\n")
    .map((l) => {
      const hard = HARD_BREAK.test(l);
      const stripped = l.replace(IMAGE_MARKER, "");
      const lead = /^[ \t]*/.exec(stripped)![0];
      const body = stripped.trim().replace(/[ \t]+/g, " ");
      if (body === "") return "";
      return `${lead}${hard && !body.endsWith("\\") ? `${body}  ` : body}`;
    })
    .filter((l, i, all) => l !== "" || (i > 0 && all[i - 1] !== ""))
    .join("\n")
    .trim();
}

interface RawPoint {
  lines: string[];
  /** Indentation of the line that started the point. */
  base: number;
  /** Column where the point's text starts (after its bullet): continuation lines are dedented by this much. */
  content: number;
}

/**
 * Rules:
 * - If any line starts with a bullet or a number ("-", "*", "•", "1.", "1)"), each such line starts a point and
 *   following plain lines continue it. A bulleted line indented deeper than the point's own bullet is a sub-point
 *   and stays in the point as a nested list. A blank line ends the point unless the next line is indented into
 *   it. A plain line that ends with ":" directly before a bulleted line is a heading ("Hier meine Liste:") and not
 *   a point.
 * - Otherwise every non-empty line is a point; a line ending in a hard break (Shift+Enter) continues on the next.
 * - A line with nothing but image markers belongs to the point before it (a pasted screenshot on its own line).
 * - Points without text and images are dropped.
 */
export function splitFeedbackPoints(input: string): FeedbackPoint[] {
  const lines = input.replace(/\r\n?/g, "\n").split("\n");
  const bulleted = lines.some((l) => BULLET.test(l));
  const raw: RawPoint[] = [];
  let current: RawPoint | null = null;
  let blankBefore = false;

  const nextContentLine = (from: number): string | undefined => {
    for (let j = from; j < lines.length; j++) if (lines[j]!.trim() !== "") return lines[j];
    return undefined;
  };
  const start = (first: string, base: number, content: number): RawPoint => {
    const point = { lines: [first], base, content };
    raw.push(point);
    return point;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    if (line.trim() === "") {
      if (bulleted) blankBefore = current !== null;
      else current = null;
      continue;
    }
    if (ONLY_MARKERS.test(line)) {
      const target = current ?? raw.at(-1);
      if (target) target.lines.push(line);
      else current = start(line, 0, 0);
      blankBefore = false;
      continue;
    }
    if (!bulleted) {
      if (current && HARD_BREAK.test(current.lines.at(-1)!)) current.lines.push(line.trimStart());
      else current = start(line.trimStart(), 0, 0);
      continue;
    }

    const indent = indentOf(line);
    const bullet = BULLET.exec(line);
    if (current && blankBefore) {
      if (indent >= current.content || (bullet && indent > current.base)) current.lines.push("");
      else current = null;
    }
    blankBefore = false;

    if (current && (bullet ? indent > current.base : true)) {
      current.lines.push(dedent(line, current.content));
    } else if (bullet) {
      current = start(line.slice(bullet[0].length), indent, indentOf(bullet[1]!) + bullet[2]!.length);
    } else {
      const next = nextContentLine(i + 1);
      if (line.trim().endsWith(":") && next !== undefined && BULLET.test(next)) continue;
      // A plain line only takes lines indented deeper than itself after a blank line.
      current = start(line.trim(), indent, indent + 1);
    }
  }

  const points: FeedbackPoint[] = [];
  for (const r of raw) {
    const joined = r.lines.join("\n");
    const images = [...new Set(markersIn(joined))];
    const text = tidy(joined);
    if (text === "" && images.length === 0) continue;
    points.push({ text, images });
  }
  return points;
}

/** Why a message cannot be sent as it is (null = fine). Same checks on client and server. */
export function feedbackTextProblem(
  points: readonly FeedbackPoint[],
  text: string,
): null | "empty" | "too_many_points" | "point_too_long" | "text_too_long" | "too_many_images" {
  if (text.length > FEEDBACK_LIMITS.textChars) return "text_too_long";
  if (points.length === 0) return "empty";
  if (points.length > FEEDBACK_LIMITS.points) return "too_many_points";
  if (points.some((p) => p.text.length > FEEDBACK_LIMITS.pointChars)) return "point_too_long";
  if (points.some((p) => p.images.length > FEEDBACK_LIMITS.imagesPerPoint)) return "too_many_images";
  return null;
}

const ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', "#39": "'" };

/** A point's Markdown as plain text, for titles and one-line previews: no bullets, emphasis, links or escapes. */
export function feedbackPlainText(md: string): string {
  return md
    .split("\n")
    .map((l) =>
      l
        .replace(BULLET, "$1")
        .replace(/^\s*#{1,6}\s+/, "")
        .replace(/^\s*>\s?/, "")
        .replace(/ {2,}$/, ""),
    )
    .join("\n")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/(\*\*|__|~~)(?=\S)(.+?)(?<=\S)\1/g, "$2")
    .replace(/(^|[^\\*])\*(?=\S)([^*\n]+?)(?<=\S)\*/g, "$1$2")
    .replace(/(^|[^\w\\])_(?=\S)([^_\n]+?)(?<=\S)_(?!\w)/g, "$1$2")
    .replace(/`([^`\n]*)`/g, "$1")
    .replace(/\\([\\`*_[\]~#+\-.!(){}>|])/g, "$1")
    .replace(/&(amp|lt|gt|quot|#39);/g, (_, e: string) => ENTITIES[e]!);
}

/** First line of a point as plain text, shortened to an item title. */
export function feedbackTitle(text: string, max = 80): string {
  const first = feedbackPlainText(text).split("\n")[0]!.trim();
  if (first.length <= max) return first;
  const cut = first.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

// ---------------------------------------------------------------------------------------------------------
// What the tester sees
// ---------------------------------------------------------------------------------------------------------

export const FEEDBACK_ENTRY_STATES = ["open", "linked", "dismissed", "withdrawn"] as const;
export type FeedbackEntryState = (typeof FEEDBACK_ENTRY_STATES)[number];

export const TESTER_STATUSES = ["received", "planned", "in_progress", "done", "not_planned"] as const;
export type TesterStatus = (typeof TESTER_STATUSES)[number];

/**
 * The tester-facing status of a point. A linked point follows its item: open categories are "planned",
 * started is "in progress", completed is "done", canceled (or a deleted item) is "not planned".
 */
export function testerStatus(
  state: FeedbackEntryState,
  item: { category: StatusCategory; deleted: boolean } | null,
): TesterStatus {
  if (state === "dismissed") return "not_planned";
  if (state !== "linked") return "received";
  if (!item || item.deleted) return "not_planned";
  switch (item.category) {
    case "started":
      return "in_progress";
    case "completed":
      return "done";
    case "canceled":
      return "not_planned";
    default:
      return "planned";
  }
}

// ---------------------------------------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------------------------------------

export const FeedbackLocale = z.enum(["en", "de"]);

export const FeedbackLinkCreate = z.object({
  label: z.string().trim().min(1).max(FEEDBACK_LIMITS.labelChars),
  productIds: z.array(z.uuid()).min(1).max(30),
  locale: FeedbackLocale.default("de"),
});
export type FeedbackLinkCreate = z.infer<typeof FeedbackLinkCreate>;

export const FeedbackLinkPatch = z.object({
  label: z.string().trim().min(1).max(FEEDBACK_LIMITS.labelChars).optional(),
  productIds: z.array(z.uuid()).min(1).max(30).optional(),
  locale: FeedbackLocale.optional(),
});
export type FeedbackLinkPatch = z.infer<typeof FeedbackLinkPatch>;

/** One submission from the tester page. The server splits `text` itself with `splitFeedbackPoints`. */
export const FeedbackBatchCreate = z.object({
  productId: z.uuid(),
  text: z.string().max(FEEDBACK_LIMITS.textChars),
  /** Uploaded images by marker number ("[Bild 2]" -> 2). */
  images: z
    .array(z.object({ marker: z.number().int().min(1).max(999), attachmentId: z.uuid() }))
    .max(FEEDBACK_LIMITS.imagesPerBatch)
    .default([]),
  /** "Still not working": the earlier point this one follows up on. */
  followUpOfEntryId: z.uuid().optional(),
});
export type FeedbackBatchCreate = z.infer<typeof FeedbackBatchCreate>;

const Note = z.string().trim().max(FEEDBACK_LIMITS.noteChars).optional();
const EntryIds = z.array(z.uuid()).min(1).max(FEEDBACK_LIMITS.points);

export const FeedbackResolve = z.discriminatedUnion("action", [
  /**
   * One new item per point (titles from each point's first line unless `title` is given for a single point).
   * `parentId` files them all under an existing item.
   */
  z.object({
    action: z.literal("create"),
    entryIds: EntryIds,
    title: z.string().trim().min(1).max(500).optional(),
    typeId: z.uuid().optional(),
    productId: z.uuid().optional(),
    parentId: z.uuid().optional(),
    note: Note,
  }),
  /**
   * A new parent item ("master list") plus one child item per point. Without `parentTypeId` the parent gets the
   * default type; without `typeId` the children get the first type allowed below the parent (Task > Sub-task).
   */
  z.object({
    action: z.literal("createWithParent"),
    entryIds: EntryIds,
    parentTitle: z.string().trim().min(1).max(500),
    parentTypeId: z.uuid().optional(),
    typeId: z.uuid().optional(),
    productId: z.uuid().optional(),
    note: Note,
  }),
  /** One item for several points (duplicates from different testers). */
  z.object({
    action: z.literal("createMerged"),
    entryIds: EntryIds,
    title: z.string().trim().min(1).max(500),
    typeId: z.uuid().optional(),
    productId: z.uuid().optional(),
    note: Note,
  }),
  /** Attach the points to an existing item; `reopen` moves a closed item back to the first open status. */
  z.object({
    action: z.literal("link"),
    entryIds: EntryIds,
    itemId: z.uuid(),
    reopen: z.boolean().optional(),
    note: Note,
  }),
  z.object({ action: z.literal("dismiss"), entryIds: EntryIds, note: Note }),
  /** Undo: back to open. Items that were created from these points (and nothing else) go to the trash. */
  z.object({ action: z.literal("reopen"), entryIds: EntryIds }),
]);
export type FeedbackResolve = z.infer<typeof FeedbackResolve>;

/**
 * The owner corrects a point after the fact. `text`: the point's wording (the tester's original message stays
 * on the batch). `productId`: another app of the same tester link. `note`: the message to the tester on a
 * resolved point (null removes it).
 */
export const FeedbackEntryUpdate = z
  .object({
    text: z.string().trim().max(FEEDBACK_LIMITS.pointChars).optional(),
    productId: z.uuid().optional(),
    note: z.string().trim().max(FEEDBACK_LIMITS.noteChars).nullable().optional(),
  })
  .refine((v) => v.text !== undefined || v.productId !== undefined || v.note !== undefined, {
    message: "Nothing to change",
  });
export type FeedbackEntryUpdate = z.infer<typeof FeedbackEntryUpdate>;

// ---------------------------------------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------------------------------------

/** A point as the tester sees it. Never contains item keys or titles. */
export interface TesterEntryView {
  id: string;
  productId: string;
  text: string;
  imageIds: string[];
  status: TesterStatus;
  /** The owner's note (shown with "not planned", optional otherwise). */
  note: string | null;
  createdAt: string;
  /** When the tester-visible status last changed (drives the "new" badge). */
  statusChangedAt: string;
  followUpOfEntryId: string | null;
  /** Still open, so the tester may withdraw it. */
  canWithdraw: boolean;
}

export interface TesterPageView {
  label: string;
  /** Who the tester reports to (the link's creator). */
  ownerName: string;
  locale: "en" | "de";
  products: { id: string; name: string }[];
  entries: TesterEntryView[];
  /** The previous visit (before this one), for "new since your last visit". */
  lastSeenAt: string | null;
}

export interface FeedbackLinkView {
  id: string;
  label: string;
  locale: "en" | "de";
  productIds: string[];
  createdAt: string;
  createdBy: string | null;
  lastSeenAt: string | null;
  lastSubmittedAt: string | null;
  revokedAt: string | null;
  openCount: number;
  /** The URL can be read again (`GET /feedback/links/:id/url`). False for links from before sealed storage: reset them. */
  copyable: boolean;
}

/** Returned when a link is created, reset or regenerated, and by `GET /feedback/links/:id/url` (link omitted). */
export interface FeedbackLinkSecret {
  link: FeedbackLinkView;
  url: string;
}

export interface FeedbackItemRef {
  id: string;
  key: string;
  title: string;
  statusId: string;
  category: StatusCategory;
  deleted: boolean;
}

export interface FeedbackEntryView {
  id: string;
  batchId: string;
  linkId: string;
  productId: string;
  position: number;
  text: string;
  imageIds: string[];
  state: FeedbackEntryState;
  item: FeedbackItemRef | null;
  /** The item was created from this point (undo trashes it). */
  itemCreated: boolean;
  note: string | null;
  followUpOf: { entryId: string; text: string; item: FeedbackItemRef | null } | null;
  createdAt: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
}

export interface FeedbackBatchView {
  id: string;
  linkId: string;
  productId: string;
  rawText: string;
  createdAt: string;
}

export interface FeedbackList {
  entries: FeedbackEntryView[];
  batches: FeedbackBatchView[];
  links: { id: string; label: string }[];
  /** Open points across everything the caller may manage (sidebar badge). */
  openCount: number;
  /** Creation time of the oldest open point. */
  oldestOpenAt: string | null;
}

export interface FeedbackResolveResult {
  entries: FeedbackEntryView[];
  /** Items created by this call (with a parent: the parent first, then its sub-items). */
  createdItemIds: string[];
  /** Their keys, in the same order. */
  createdKeys: string[];
}

/** Payload of the `feedback.changed` event: something in the queue changed for these products. */
export interface FeedbackChangedPayload {
  linkId: string;
  entryIds: string[];
  /** Points that just arrived (a new submission), for the live announcement. */
  submitted: number;
  label: string;
}
